import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Autocomplete } from './components/Autocomplete';
import { SearchContainer } from './Search.styles';
import { useEsriMapContext } from '../../EsriMapContext';
import { GeocoderResult, SearchEndpoint } from './types';
import { useSearchLogic } from './useSearchLogic';

export interface SearchApi {
	setSearch: (term: string) => void;
}

interface SearchProps {
	searchEndpoints?: SearchEndpoint[];
	onSearch?: (result: GeocoderResult) => void;
	/** When set, renders the search UI into an external container with this id
	 * instead of embedding it inside the map. Used with the EsriGeocoder component.
	 */
	geocoderId?: string | null;
	restrictSearchToBounds?: boolean;
	/** Called once with an API object that lets callers programmatically set the search term. */
	onSearchApi?: (api: SearchApi) => void;
}

export const Search: React.FC<SearchProps> = ({
	searchEndpoints,
	onSearch,
	geocoderId,
	restrictSearchToBounds,
	onSearchApi,
}): JSX.Element | null => {
	// Context is consumed here and passed explicitly to the hook,
	// keeping the hook free of context dependencies.
	const { view, layerSelectionApi, getLayerIdsByServiceId, selectionApi } =
		useEsriMapContext();

	const { onInputChange, onSelect, defaultSearch, setDefaultSearch, textFieldProps } =
		useSearchLogic({
			searchEndpoints,
			onSearch,
			geocoderId,
			view,
			layerSelectionApi,
			getLayerIdsByServiceId,
			selectionApi,
			restrictSearchToBounds,
		});

	useEffect(() => {
		onSearchApi?.({ setSearch: setDefaultSearch });
	}, [onSearchApi, setDefaultSearch]);

	// Resolve the external portal target after mount so the external div
	// is guaranteed to exist in the DOM.
	const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
	useEffect(() => {
		if (!geocoderId) {
			setPortalTarget(null);
			return;
		}
		setPortalTarget(document.getElementById(geocoderId));
	}, [geocoderId]);

	const autocomplete = (
		<Autocomplete
			onInputChange={onInputChange}
			onSelect={onSelect}
			defaultSearch={defaultSearch}
			TextFieldProps={textFieldProps}
		/>
	);

	if (geocoderId) {
		return portalTarget ? createPortal(autocomplete, portalTarget) : null;
	}

	return <SearchContainer>{autocomplete}</SearchContainer>;
};
